import { create } from 'zustand';
import type { User } from '@/types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, accessToken: string) => void;
  clearAuth: () => void;
  updateUser: (user: User) => void;
  initialize: () => void;
}

const getStoredAuth = (): { user: User | null; accessToken: string | null } => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    return { user: null, accessToken: null };
  }

  // Try to get user from localStorage if persisted
  try {
    const storedUser = localStorage.getItem('auth-user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    return { user, accessToken };
  } catch {
    return { user: null, accessToken };
  }
};

export const useAuthStore = create<AuthState>((set) => {
  const stored = getStoredAuth();
  
  return {
    user: stored.user,
    accessToken: stored.accessToken,
    isAuthenticated: !!stored.accessToken,
    setAuth: (user, accessToken) => {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('auth-user', JSON.stringify(user));
      set({ user, accessToken, isAuthenticated: true });
    },
    clearAuth: () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('auth-user');
      set({ user: null, accessToken: null, isAuthenticated: false });
    },
    updateUser: (user) => {
      localStorage.setItem('auth-user', JSON.stringify(user));
      set((state) => ({ ...state, user }));
    },
    initialize: () => {
      const stored = getStoredAuth();
      set({
        user: stored.user,
        accessToken: stored.accessToken,
        isAuthenticated: !!stored.accessToken,
      });
    },
  };
});
